import moment from 'moment';
import {CarePlanConfigurationFeature} from '../backend/enums/care-plan-configuration-feature';
import {CarePlanConfigurationType} from '../backend/enums/care-plan-configuration-type';
import {CarePlanConfigurationItemAutocompleteItem} from '../backend/interfaces/care-plan-configuration-item-autocomplete-item';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../backend/requests/care-plan-activities-group';
import {Option} from '../components/select-dropdown/select-dropdown';
import {ConfigurationsIndex} from '../types';
import {Logger} from '../utils/managers/logger';

export class DynamicFormManager {
  // Singleton Instance
  private static _instance: DynamicFormManager;

  // Private
  private logger: Logger;

  static get instance(): DynamicFormManager {
    if (!this._instance) {
      this._instance = new DynamicFormManager();
    }
    return this._instance;
  }

  // Constructor
  constructor() {
    // Create Log
    this.logger = new Logger('DynamicFormManager');
  }

  private mergeDateTime(date: moment.Moment, time: moment.Moment): moment.Moment {
    const dateClone = date.clone();
    dateClone.set({
      hour: time.get('hour'),
      minute: time.get('minute'),
      second: 0,
      millisecond: 0,
    });
    return dateClone;
  }

  public remapCarePlanConfigurationValue(
    configurationsIndex: ConfigurationsIndex,
    data: Record<string, any>
  ): Array<CarePlanActivitiesGroupApiRequestConfigurationsItem> {
    // Check configurationsIndex
    if (!configurationsIndex) return [];

    // Map
    const entries = Object.entries(data);

    // Map entries
    return entries
      .map(([key, value]: [string, any]) => {
        // Map
        let carePlanConfigurationId = key;
        let carePlanConfigurationValue:
          | string
          | number
          | boolean
          | Option
          | Option[]
          | CarePlanConfigurationItemAutocompleteItem
          | unknown = value;
        const field = configurationsIndex[carePlanConfigurationId];

        // Check Field type
        switch (field.type) {
          case CarePlanConfigurationType.Time:
            // Check Feature
            if (field.feature === CarePlanConfigurationFeature.StartsAtTime) {
              // Look for Start At Date feature
              const startsAtDate = entries.find(([key, value]: [string, any]) => {
                const field = configurationsIndex[key];
                if (field.feature === CarePlanConfigurationFeature.StartsAtDate) {
                  return value;
                }
                return undefined;
              });

              // Eval
              if (startsAtDate) {
                carePlanConfigurationValue = this.mergeDateTime(
                  moment(startsAtDate[1] as any),
                  moment(carePlanConfigurationValue as any)
                ).utc();
              }
            } else if (moment.isMoment(carePlanConfigurationValue)) {
              carePlanConfigurationValue = carePlanConfigurationValue.clone().utc();
            }
            break;
          case CarePlanConfigurationType.Date:
            // Check Feature
            if (field.feature === CarePlanConfigurationFeature.StartsAtDate) {
              // Look for Start At Time feature
              const startsAtTime = entries.find(([key, value]: [string, any]) => {
                const field = configurationsIndex[key];
                if (field.feature === CarePlanConfigurationFeature.StartsAtTime) {
                  return value;
                }
                return undefined;
              });

              // Eval
              if (startsAtTime) {
                carePlanConfigurationValue = this.mergeDateTime(
                  moment(carePlanConfigurationValue as any),
                  moment(startsAtTime[1] as any)
                ).utc();
              }
            } else if (moment.isMoment(carePlanConfigurationValue)) {
              carePlanConfigurationValue = carePlanConfigurationValue.clone().utc();
            }
            break;
          case CarePlanConfigurationType.Array:
            if (
              field?.feature === CarePlanConfigurationFeature.AutonomyAssessment ||
              !field.multipleValues
            ) {
              carePlanConfigurationValue = (carePlanConfigurationValue as Option)?.key;
            } else {
              carePlanConfigurationValue = (carePlanConfigurationValue as Option[])?.map(
                (item: any) => item?.key
              );
            }
            break;

          default:
            break;
        }

        return {
          carePlanConfigurationId,
          carePlanConfigurationValue,
        };
      })
      .filter(
        (item: any) => item !== undefined
      ) as Array<CarePlanActivitiesGroupApiRequestConfigurationsItem>;
  }
}
